/**
 * The dialog's form view (Option C, C1 + C2 + C3): the rail or the compact bar (create mode), and the pane — header, the
 * sectioned form (`ConnectionFormFields`), the docked result strip and the footer — laid out by `ConnectionDialogShell`.
 * In the short tier the strip is a one-line summary whose Details open the full strip at the top of the scroll region.
 * Above the form (C3): what a nested flow saved, the paste field (preview, then Apply) and the applied-paste summary.
 */
import { useState, type ReactNode } from 'react';
import { useStore } from '@tanstack/react-store';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { UserFacingEngineId } from '@/core/engines';
import type { AzureDiscoveredResource } from '@/types/azureDiscovery';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { ConnectionFormFields } from '../../components/ConnectionFormFields';
import { ConnectionTestResults } from '../../components/ConnectionTestResults';
import { focusLegRemedy } from '../../components/storageLegRemedy';
import type { useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { ConnectionForm } from '../state/connectionForm';
import type { useEngineDrafts } from '../state/useEngineDrafts';
import type { DialogLayout } from '../hooks/useDialogLayout';
import type { DialogNavigation } from '../hooks/useDialogNavigation';
import type { EntryPointsApi } from '../hooks/useEntryPoints';
import type { useConnectionTestRun } from '../hooks/useConnectionTestRun';
import type { useLoadDatabases } from '../hooks/useLoadDatabases';
import type { useValidationGate } from '../hooks/useValidationGate';
import type { PasteEntry } from '../hooks/usePasteEntry';
import { PastePaneField } from '../paste/PastePaneField';
import { PasteAppliedLine } from '../paste/PasteAppliedLine';
import { engineLabelOf } from '../paste/pastePreview';
import { EngineRail } from '../rail/EngineRail';
import { RailEntryPoints } from '../rail/RailEntryPoints';
import { CompactEngineBar } from '../rail/CompactEngineBar';
import { ConnectionDialogShell } from './ConnectionDialogShell';
import { ConnectionPaneHeader } from './ConnectionPaneHeader';
import { ConnectionTestSummary } from './ConnectionTestSummary';
import { resultStripModel } from './resultStripModel';
import { useErrorFieldInView } from './useErrorFieldInView';
import { ConnectionDialogFooter } from './ConnectionDialogFooter';
import { FORM_CONTAINER_CLASS } from './formLayout';
import { isEntraAuth, requiredEmptyFields } from '../state/connectionFieldRules';
import { fieldRuleLabel } from '../fields/fieldLabels';

type Props = {
  form: ConnectionForm;
  engine: DatabaseEngine;
  authentication: ConnectionFormValues['authentication'];
  isEditMode: boolean;
  layout: DialogLayout;
  drafts: ReturnType<typeof useEngineDrafts>;
  nav: DialogNavigation;
  entries: EntryPointsApi;
  paste: PasteEntry;
  /** What an import or Detect add saved while the dialog stayed open (C3). */
  notice: ReactNode;
  tests: ReturnType<typeof useConnectionTestOutcome>;
  testRun: ReturnType<typeof useConnectionTestRun>;
  loads: ReturnType<typeof useLoadDatabases>;
  gate: ReturnType<typeof useValidationGate>;
  onSubmitForm: () => void;
  onApplyFix: Parameters<typeof ConnectionTestResults>[0]['onApplyFix'];
  onSaveAnyway: () => void;
  onAzurePick: (resource: AzureDiscoveredResource) => void;
  savingReason?: string;
  deleting: boolean;
  deleteError?: string;
  onDelete?: () => void;
  onCancel: () => void;
  track: TrackFunction;
};

export function ConnectionFormView(props: Props) {
  const { form, engine, isEditMode, layout, drafts, nav, entries, paste, tests, testRun, loads, gate, track } = props;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const pick = (source: 'rail' | 'compact_select') => (next: UserFacingEngineId) => nav.pick(next, source);
  // One strip for every engine and every test path (C2): the same model feeds the docked strip and the short tier's line.
  // `shown`, not `visible`: the strip's X closes the answer on screen until the next one arrives.
  const strip = resultStripModel(tests.shown, { testing: testRun.testRun !== null, isEditMode });
  // The applied line's "Still needed", read live (joined: the selector's result compares by value).
  const stillNeededText = useStore(form.store, (state) => requiredEmptyFields(state.values).map((rule) => fieldRuleLabel(rule.field, state.values)).join('\n'));
  const stillNeeded = stillNeededText ? stillNeededText.split('\n') : [];
  // Its "No password needed" follows the sign-in on screen, not the one an Azure pick set (uxfix review UXF-02).
  const entraSignIn = useStore(form.store, (state) => isEntraAuth(state.values.authentication));
  useErrorFieldInView(tests.visible, strip?.fieldSelector);
  const dismissResult = (surface: 'strip' | 'summary') => {
    if (strip) track('feature:connection_test_result_dismiss', { engine, tone: strip.tone, surface });
    tests.dismiss();
    setDetailsOpen(false);
  };

  const testResults = (inline: boolean) => (
    <ConnectionTestResults
      model={strip}
      inline={inline}
      isEditMode={isEditMode}
      busy={testRun.testRun !== null || props.deleting}
      onLegAction={(actionId) => focusLegRemedy(actionId, track)}
      onApplyFix={props.onApplyFix}
      onSaveAnyway={props.onSaveAnyway}
      onDismiss={() => dismissResult('strip')}
      watchdog={{
        visible: testRun.watchdogVisible,
        offerSaveAnyway: testRun.testRun?.source === 'submit',
        onSaveAnyway: () => {
          track('feature:connection_test_watchdog', { action: 'save_anyway' });
          // Stop waiting on the hung run FIRST, or its late result would still flow through the Save.
          testRun.abort();
          props.onSaveAnyway();
        },
        onKeepWaiting: () => {
          track('feature:connection_test_watchdog', { action: 'keep_waiting' });
          testRun.dismissWatchdog();
        },
        onCancel: () => {
          track('feature:connection_test_watchdog', { action: 'cancel' });
          testRun.abort();
        },
        deleting: props.deleting,
      }}
    />
  );

  return (
    <ConnectionDialogShell
      layout={layout}
      isEditMode={isEditMode}
      engine={engine}
      rail={<EngineRail engine={engine} enginesWithInput={drafts.enginesWithInput} onPick={pick('rail')} onAllEngines={() => nav.back('button')} />}
      railEntries={<RailEntryPoints entries={entries} pasteOpen={paste.open} />}
      compactBar={<CompactEngineBar engine={engine} onPick={pick('compact_select')} onAllEngines={() => nav.back('button')} entries={entries} savingReason={props.savingReason} />}
      paneHeader={<ConnectionPaneHeader engine={engine} short={layout.short} />}
      results={
        layout.short ? (
          <ConnectionTestSummary model={strip} watchdogVisible={testRun.watchdogVisible} expanded={detailsOpen} onToggle={() => setDetailsOpen((open) => !open)} onDismiss={() => dismissResult('summary')} />
        ) : (
          testResults(false)
        )
      }
      scrollContent={
        <>
          {layout.short && detailsOpen && testResults(true)}
          {props.notice}
          {!isEditMode && paste.open && (
            <PastePaneField
              engine={engine}
              baseValuesFor={drafts.baseValuesFor}
              text={paste.text}
              textVia={paste.textVia}
              onTextChange={paste.setText}
              onApply={nav.applyPanePaste}
              onClose={nav.closePasteField}
              focusRequest={paste.focusRequest}
              track={track}
            />
          )}
          {!isEditMode && paste.applied?.engine === engine && <PasteAppliedLine applied={paste.applied} stillNeeded={stillNeeded} entraSignIn={entraSignIn} />}
          <form
            id="connection-form"
            data-testid="connection-form"
            className={FORM_CONTAINER_CLASS}
            onSubmit={(event) => {
              event.preventDefault();
              props.onSubmitForm();
            }}
          >
            <ConnectionFormFields
              form={form}
              engine={engine}
              authentication={props.authentication}
              loadedDatabases={drafts.visibleDatabases}
              onLoadDatabases={() => void loads.load()}
              loadingDatabases={loads.loading}
              defaultHint={gate.defaultHint}
              serverCarriedFrom={drafts.serverCarriedFrom ? engineLabelOf(drafts.serverCarriedFrom) : null}
              onDismissDefaultHint={gate.dismissDefaultHint}
              onAzurePick={props.onAzurePick}
              entryDisabledReason={props.savingReason}
            />
          </form>
        </>
      }
      notices={
        // A delete main REFUSED (round 5): the record is kept, and the dialog says so instead of a dead click.
        props.deleteError && (
          <p className="shrink-0 px-6 pb-2 text-sm text-destructive" role="alert" data-testid="delete-connection-error">
            {props.deleteError}
          </p>
        )
      }
      footer={
        <ConnectionDialogFooter
          isEditMode={isEditMode}
          short={layout.short}
          testing={testRun.testRun?.source ?? null}
          deleting={props.deleting}
          onTest={() => void testRun.run(form.state.values, false, 'test')}
          onDelete={props.onDelete}
          onCancel={props.onCancel}
        />
      }
    />
  );
}
